import { useInfiniteScrollTable } from "@flaminem/ui-components";
import { FC } from "react";
import { useNavigate } from "react-router";

import { DimensionedLayout } from "src/components/DimensionedLayout/DimensionedLayout";
import { useDimensions } from "src/components/DimensionedLayout/useDimensions";
import { InfiniteScrollTable } from "src/components/Tables";
import { defaultRowHeight } from "src/config/table-config";
import { CustomerCasesTable } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.types";
import { useGetCustomerCasesQuery } from "src/graphql/anti-fraud/queries/customers/GetCustomerCasesQuery";
import { useSearch } from "src/hooks/useSearch";
import { useTableURLParams } from "src/hooks/useTableURLParams";
import { useT } from "src/internationalization";
import { CaseForCustomer } from "src/types/customer";

import { WidgetDefinition } from "../Widget.typings";
import WidgetContainer, { WidgetContainerProps } from "../WidgetContainer";

import { CustomerCasesToolbar } from "./CustomerCasesToolbar";
import { useGetColumns } from "./useGetColumns";

export type CustomerCasesWidgetProps = {
  data?: {
    id: string;
  };
  widgetDefinition: WidgetDefinition<{ customerCasesTable: CustomerCasesTable }>;
} & Omit<WidgetContainerProps, "children">;

const CustomerCasesWidget: FC<CustomerCasesWidgetProps> = ({
  data,
  widgetConfig,
  rowConfiguration,
  widgetDefinition,
}) => {
  const navigate = useNavigate();
  const t = useT();

  const { pageSize, dimensions } = useDimensions();
  const { offset, searchTerm, handleOnSearchTermChange } = useTableURLParams({}, {});
  const { searchLoading, filteredData, searchError, refetch } = useSearch({
    searchQueryHook: useGetCustomerCasesQuery,

    searchParams: {
      id: data?.id,
      limit: pageSize,
      offset: offset,
      searchTerm: searchTerm,
      caseTypeCode: "",
    },
  });

  const { data: casesData, totalCount } = useInfiniteScrollTable<CaseForCustomer>(
    filteredData?.cases ?? [],
    filteredData?.pageInfo?.totalCount ?? 0,
    filteredData?.pageInfo?.limit ?? pageSize,
    filteredData?.pageInfo?.offset ?? offset
  );

  const columns = useGetColumns({
    customerCasesTable: widgetDefinition.configuration?.customConfiguration?.customerCasesTable,
  });

  const handleLoadMore = async (startIndex: number, stopIndex: number) => {
    if (!searchLoading) {
      refetch({
        limit: stopIndex - startIndex + 1,
        offset: startIndex,
        searchString: searchTerm && searchTerm?.length > 0 ? searchTerm : null,
      });
    }
  };

  return (
    <>
      <WidgetContainer
        widgetConfig={widgetConfig}
        widgetDefinition={widgetDefinition}
        rowConfiguration={rowConfiguration}
      >
        <DimensionedLayout>
          {dimensions?.width && (
            <InfiniteScrollTable
              id="customer-cases"
              // const [search, setSearch] = useState("");
              // display only if parent not have
              description={
                !widgetDefinition.description
                  ? t("page.customers.table.customer.cases.description")
                  : undefined
              }
              title={
                !widgetDefinition.title ? t("page.customers.table.customer.cases.tile") : undefined
              }
              className={"flex-1"}
              totalCount={totalCount}
              rowHeight={defaultRowHeight}
              fetchMore={handleLoadMore}
              scrollToIndex={totalCount && offset ? offset : -1}
              toolbar={() => (
                <CustomerCasesToolbar
                  handleOnSearchTermChange={handleOnSearchTermChange}
                  searchTerm={searchTerm}
                  placeholder={
                    widgetDefinition.configuration?.customConfiguration?.customerCasesTable
                      .searchPlaceholder
                  }
                />
              )}
              data={casesData ?? []}
              isLoading={searchLoading}
              columns={columns}
              pageSize={pageSize}
              errorText={searchError?.message}
              isError={searchError != null}
              onRowClick={(row) => navigate(`/case/${row.data?.typeCode}/${row.data?.id}`)}
            />
          )}
        </DimensionedLayout>
      </WidgetContainer>
    </>
  );
};

export default CustomerCasesWidget;
